"use client";

import { trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import { buildTrackedPaywallUrl, getBrowserCouponCode } from "@/lib/paywallUrl";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { type ReactNode, useEffect, useId, useState } from "react";

interface PricingButtonProps {
  user?: { id: string; email?: string } | null;
  fallback?: ReactNode;
}

export function PricingButton({ user, fallback = null }: PricingButtonProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const offerDescriptionId = useId();
  const [destination, setDestination] = useState({
    href: "/paywall?source=top_nav_upgrade_button&return_to=%2F",
    coupon: "",
  });

  useEffect(() => {
    // Read browser persistence after hydration; refresh on client navigation.
    setDestination({
      href: buildTrackedPaywallUrl({
        source: "top_nav_upgrade_button",
      }),
      coupon: getBrowserCouponCode()?.toLowerCase() ?? "",
    });
  }, [pathname, searchParams]);

  if (!destination.coupon) return fallback;

  const recognizedCoupon =
    destination.coupon.endsWith("33") ||
    destination.coupon === "birdsofafeather" ||
    destination.coupon === "savestate";
  const offerLabel = recognizedCoupon
    ? destination.coupon === "blackfriday33"
      ? "Black Friday offer"
      : "Offer available"
    : null;

  return (
    <div className="relative shrink-0">
      <Link
        href={destination.href}
        prefetch={false}
        aria-describedby={offerLabel ? offerDescriptionId : undefined}
        onClick={() =>
          trackUpgradeIntent(posthog, "top_nav_upgrade_button", {
            ...(user ? { user_id: user.id, email: user.email } : {}),
            cta_label: "Pricing",
            offer_indicated: recognizedCoupon,
          })
        }
        className="inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-lg border border-white/12 bg-black/20 px-2 text-sm font-semibold text-white/90 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:gap-2 sm:px-3"
      >
        Pricing
      </Link>
      {offerLabel && (
        <span
          id={offerDescriptionId}
          className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium leading-tight text-[#FD954F]"
        >
          {offerLabel}
        </span>
      )}
    </div>
  );
}
